/**
 * live-selectors.ts — store-bound answers to "what is running right now".
 *
 * AX14 (cockpit-excellence). The predicates themselves are pure and
 * live in `~/lib/conv-state`; this module is the thin binding to
 * `chatStore` so every consumer — the roadmap queue bar, the chat
 * scope strip, the agents rail — reads the SAME truth. Three
 * divergent copies used to answer this question, and the operator saw
 * the result: the chat showed a run in progress while the queue bar
 * still offered to start it.
 *
 * Import these instead of re-deriving from `chatStore.state.convs`.
 */

import { chatStore } from '~/state/chat';
import {
  ARCHITECT_CONV_ID,
  isConvWorkingFrom,
  pickCoordinatorConv,
  pickUnanchoredLiveConvs,
  type ConvStateLike,
} from '~/lib/conv-state';

/** True when the conv has an assistant turn in flight. */
export function isConvWorking(conv: string): boolean {
  const snap = chatStore.state.convs[conv] ?? null;
  return isConvWorkingFrom(snap, chatStore.state.convMap[conv]);
}

/**
 * The architect conv the cockpit's Run All / Stop controls act on, or
 * null when the roster has none.
 *
 * Prefers the daemon snapshot (`convs`); falls back to locally known
 * conv metadata so a conv created this session — before the next
 * snapshot lands — is still addressable.
 */
export function activeArchitectConv(): string | null {
  // BT-19 — the Architect is the only coordinator; the retired
  // Orchestrator's `roadmap-architect-*` convs are history, never a target.
  const fromSnapshot = pickCoordinatorConv(Object.values(chatStore.state.convs));
  if (fromSnapshot) return fromSnapshot;
  const local: Array<ConvStateLike & { member?: string | null }> = Object.entries(chatStore.state.convMeta).map(
    ([conv, meta]) => ({ conv, member: meta.member ?? null, archived: !!chatStore.state.archivedConvs[conv] }),
  );
  return pickCoordinatorConv(local) ?? ARCHITECT_CONV_ID;
}

/** True when the architect is mid-run — the queue bar's "is it busy". */
export function isArchitectWorking(): boolean {
  const conv = activeArchitectConv();
  if (!conv) return false;
  // BT-19 — between turns a running queue is the Architect WAITING on a
  // delegate (`current_action`), which is still "running".
  return isConvWorking(conv) || !!chatStore.state.convs[conv]?.current_action;
}

/**
 * LAL9 — live convs that skipped their `⟦anchor⟧` marker this turn.
 * While non-empty the roadmap's WORKING highlight may point at a stale
 * initiative (the conv keeps its previous `initiative_id`); the queue
 * bar renders a warning instead of failing silent.
 */
export function unanchoredLiveConvs(): string[] {
  return pickUnanchoredLiveConvs(
    Object.values(chatStore.state.convs),
    chatStore.state.anchorMissing,
  );
}
